<script>
import { SOCIAL_MEDIA_LINKS, socialProfilesOf } from './SocialIcons.vue';

/**
 * A fileira de redes da seção "Perfis sociais" do painel de detalhes.
 *
 * A diferença para o `SocialIcons.vue`, que mostra só o que existe: aqui
 * aparecem SEMPRE as nove redes que a aplicação conhece. Rede preenchida vem
 * na cor da marca e leva ao perfil; rede vazia fica cinza e não é link — é
 * assim que a seção responde "o que falta cadastrar" sem precisar de texto.
 *
 * Por isso o vazio é um `<span>`, e não um `<a>` desabilitado: âncora sem
 * `href` continua no caminho da tecla Tab e não tem estado desabilitado no
 * HTML. O `<span>` sai da ordem de foco sozinho.
 */
export default {
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    profiles() {
      const profiles = socialProfilesOf(this.contact);

      return SOCIAL_MEDIA_LINKS.map(media => {
        const handle = profiles[media.key];
        // O `wa.me` pede o numero em digitos, no formato internacional -- o
        // telefone do contato vem em E.164, com o `+` na frente. O que o
        // usuario LE continua sendo o numero cheio; so o endereco perde a
        // pontuacao.
        const alvo =
          media.key === 'phone' && handle ? handle.replace(/\D/g, '') : handle;

        return {
          ...media,
          handle: handle || '',
          href: handle ? `${media.link}${alvo}` : '',
        };
      });
    },
  },
  methods: {
    ariaLabelFor(profile) {
      const value = profile.handle || this.$t('CONTACT_PANEL.FIELDS.EMPTY');
      return `${profile.label}: ${value}`;
    },
    // `left`, e nao `top`: e a unica posicao que nao depende do Popper acertar
    // o limite da tela. Medido nesta fileira, com o painel na ultima coluna, a
    // dica do ultimo icone nascia em 1843→1992 numa janela de 1920 -- 72px
    // fora. Nem `top-end` nem `boundariesElement: 'viewport'` a trouxeram de
    // volta (o padrao do projeto e `'window'`, que no Popper 1 e o DOCUMENTO
    // inteiro, e nao a area visivel). Deitada para a esquerda ela cresce para
    // dentro da tela por geometria, sem depender de correcao nenhuma.
    tooltipFor(profile) {
      return {
        content: this.ariaLabelFor(profile),
        placement: 'left',
        boundariesElement: 'viewport',
      };
    },
  },
};
</script>

<template>
  <div class="flex flex-wrap items-center w-full gap-3">
    <template v-for="profile in profiles">
      <a
        v-if="profile.href"
        :key="profile.key"
        v-tooltip="tooltipFor(profile)"
        :href="profile.href"
        target="_blank"
        rel="noopener noreferrer nofollow"
        :aria-label="ariaLabelFor(profile)"
        class="transition-transform duration-200 ease-out social-profile hover:scale-125"
        :class="`social-profile--${profile.key}`"
      >
        <fluent-icon :icon="profile.icon" size="20" />
      </a>
      <span
        v-else
        :key="profile.key"
        v-tooltip="tooltipFor(profile)"
        :aria-label="ariaLabelFor(profile)"
        class="cursor-default text-line-strong dark:text-subtle"
      >
        <fluent-icon :icon="profile.icon" size="20" />
      </span>
    </template>
  </div>
</template>

<style lang="scss" scoped>
// Cor de marca não é token de design: não existe `text-facebook` nem faria
// sentido existir, porque esses valores não são nossos e não acompanham o tema.
// Ficam aqui, literais e num lugar só, em vez de espalhados pelo template.
.social-profile--facebook {
  color: #1877f2;
}
.social-profile--twitter {
  color: #1da1f2;
}
.social-profile--linkedin {
  color: #0a66c2;
}
.social-profile--github {
  color: #181717;
}
.social-profile--instagram {
  color: #e4405f;
}
.social-profile--line {
  color: #06c755;
}
.social-profile--telegram {
  color: #26a5e4;
}
.social-profile--phone {
  color: #25d366;
}

// O preto do GitHub some no tema escuro -- a própria marca troca para branco
// nesse caso. Escrito como `.dark .x`, e não com `@apply dark:`, porque aqui o
// `.dark` é o `<body>`: um seletor com escopo prega o atributo na última parte,
// que continua sendo `.social-profile--github`.
.dark .social-profile--github {
  color: #e6edf3;
}

// O site não tem marca -- o ícone é um globo genérico. Fica no azul da
// aplicação, que é o que o painel já usa para ação.
.social-profile--website {
  @apply text-brand-text;
}
</style>
